import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeProvider } from '../../contexts/ThemeContext';
import { CallsScreen } from '../../pages/CallsScreen';
import { EmergencyCallNotification } from '../ui/EmergencyCallNotification';
import { DeckPhoneFrame } from './DeckPhoneFrame';
import { IosHomeScreen } from './IosHomeScreen';
import { momEmergencyScript, momLiveCommand } from '../../data/deckSlide6';
export type EmergencyPhase = 'idle' | 'detect' | 'alert' | 'live' | 'done';
interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}
interface VexuEmergencyShowcaseProps {
  scale?: number;
  /** Changing this value restarts the guided simulation. */
  runKey?: number;
  onPhaseChange?: (phase: EmergencyPhase) => void;
  onComplete?: () => void;
}

/** The caller for this scenario — a Family contact with emergency detection on. */
const MOM_CALL = {
  name: 'Mom',
  initials: 'M',
  colorLight: 'bg-red-100 text-red-700',
  colorDark: 'bg-red-500/20 text-red-400',
  group: 'Family',
  isEmergency: true
};

/**
 * Runs the product's real emergency flow: Vexu handling calls, the emergency
 * notification, and the live emergency call with its streaming transcript.
 */
export function VexuEmergencyShowcase({
  scale = 0.86,
  runKey = 0,
  onPhaseChange,
  onComplete
}: VexuEmergencyShowcaseProps) {
  const [screen, setScreen] = useState<'home' | 'live'>('home');
  const [showAlert, setShowAlert] = useState(false);
  // The deep link must be consumed once — leaving it set would re-open (and
  // therefore reset) the live transcript on every render.
  const [openCallId, setOpenCallId] = useState<number | null>(999);
  const [focus, setFocus] = useState<Rect | null>(null);
  const [tap, setTap] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);

  // Indicators belong to the screen they were measured on.
  useEffect(() => {
    setFocus(null);
    setTap(null);
  }, [screen]);
  useEffect(() => {
    let cancelled = false;
    const timers: number[] = [];
    const sleep = (ms: number) => new Promise<void>(resolve => {
      timers.push(window.setTimeout(resolve, ms));
    });
    const measure = (el: HTMLElement): Rect | null => {
      const stage = stageRef.current;
      if (!stage) return null;
      const stageRect = stage.getBoundingClientRect();
      const k = stageRect.width / stage.offsetWidth || 1;
      const r = el.getBoundingClientRect();
      return {
        x: (r.left - stageRect.left) / k,
        y: (r.top - stageRect.top) / k,
        w: r.width / k,
        h: r.height / k
      };
    };
    const run = async () => {
      setScreen('home');
      setShowAlert(false);
      setOpenCallId(999);
      setFocus(null);
      setTap(null);
      onPhaseChange?.('idle');

      // Vexu is answering a Family call in the background.
      await sleep(1500);
      if (cancelled) return;
      onPhaseChange?.('detect');

      // Urgency detected → the emergency alert reaches the user.
      await sleep(700);
      if (cancelled) return;
      setShowAlert(true);
      onPhaseChange?.('alert');
      await sleep(1600);
      if (cancelled) return;

      // Highlight, tap, then clear every indicator BEFORE the screen changes.
      const el = stageRef.current?.querySelector<HTMLElement>('[data-sim="emergency-notification"] button');
      if (el) {
        const rect = measure(el);
        if (rect) setFocus(rect);
        await sleep(320);
        if (cancelled) return;
        if (rect) setTap({
          x: rect.x + rect.w / 2,
          y: rect.y + rect.h / 2
        });
        await sleep(180);
        if (cancelled) return;
        setTap(null);
        setFocus(null);
        await sleep(140);
      }
      if (cancelled) return;
      setShowAlert(false);
      setScreen('live');
      onPhaseChange?.('live');

      // Let the transcript, the live command and the wrap-up play out.
      await sleep(30000);
      if (cancelled) return;
      onPhaseChange?.('done');
      onComplete?.();
    };

    /** Plays the scenario, then restarts it after a short pause. */
    const loop = async () => {
      while (!cancelled) {
        await run();
        if (cancelled) return;
        await sleep(3000);
      }
    };
    loop();
    return () => {
      cancelled = true;
      timers.forEach(id => window.clearTimeout(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey]);
  return <ThemeProvider initialMode="light" data-id="element-309" data-mp="3v6a6sqry9idp">
      <DeckPhoneFrame scale={scale} data-id="element-310" data-mp="3q9s03ld279yq">
        <div ref={stageRef} className="h-full relative" data-id="element-311" data-mp="3qauu425ohsiv">
          {screen === 'home' ? <>
              <IosHomeScreen data-id="element-312" data-mp="8u38t2t345i6" />
              <AnimatePresence data-id="element-313" data-mp="42bp9c8cj01j">
                {showAlert && <div data-sim="emergency-notification" className="absolute inset-x-0 top-0 z-[62]" data-id="element-314" data-mp="40qr7cmtoyyx">
                    <EmergencyCallNotification key="alert" callerName={MOM_CALL.name} onTap={() => setScreen('live')} data-id="element-315" data-mp="37hqwk5pcaviz" />
                  </div>}
              </AnimatePresence>
            </> : <CallsScreen onNavigate={() => undefined} simulatedCall={MOM_CALL} initialCallId={openCallId} onInitialCallOpened={() => setOpenCallId(null)} emergencyScript={momEmergencyScript} liveCommandDemo={momLiveCommand} showCommandLog={false} data-id="element-316" data-mp="1e133tyrdkbmf" />}

          {/* Presentation layer */}
          <div className="pointer-events-none absolute inset-0 z-[80]" data-id="element-317" data-mp="1wprku9mqlwsx">
            <AnimatePresence data-id="element-318" data-mp="21oup3ruw7yzu">
              {focus && <motion.div key={`${focus.x}-${focus.y}-${focus.h}`} initial={{
              opacity: 0,
              scale: 1.03
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              transition: {
                duration: 0.12
              }
            }} transition={{
              duration: 0.28,
              ease: [0.22, 0.61, 0.36, 1]
            }} className="absolute rounded-[20px] ring-2 ring-vexu/70" style={{
              left: focus.x - 4,
              top: focus.y - 4,
              width: focus.w + 8,
              height: focus.h + 8,
              boxShadow: '0 0 0 3px rgba(0,200,83,0.12), 0 8px 28px -10px rgba(0,200,83,0.5)'
            }} data-id="element-319" data-mp="21rgubkuba2ew" />}
            </AnimatePresence>

            <AnimatePresence data-id="element-320" data-mp="1zazbjj1qby71">
              {tap && <motion.span key={`${tap.x}-${tap.y}`} className="absolute" style={{
              left: tap.x,
              top: tap.y
            }} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0,
              transition: {
                duration: 0.1
              }
            }} transition={{
              duration: 0.12
            }} data-id="element-321" data-mp="1zbhcuu3912lt">
                  <motion.span className="absolute block rounded-full bg-vexu/25" style={{
                left: -22,
                top: -22,
                width: 44,
                height: 44
              }} initial={{
                scale: 0.4,
                opacity: 0.8
              }} animate={{
                scale: 1,
                opacity: 0
              }} transition={{
                duration: 0.32,
                ease: 'easeOut'
              }} data-id="element-322" data-mp="1beujynhwxums" />
                  <span className="absolute block rounded-full bg-vexu/80" style={{
                left: -7,
                top: -7,
                width: 14,
                height: 14,
                boxShadow: '0 0 0 3px rgba(255,255,255,0.75)'
              }} data-id="element-323" data-mp="shjhyv4wcn3n" />
                </motion.span>}
            </AnimatePresence>
          </div>
        </div>
      </DeckPhoneFrame>
    </ThemeProvider>;
}